// src/components/modules/TAI/TAITrasladosList.js

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { List, Checkbox, Empty, Typography, Tag } from "antd";
import { CarOutlined } from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import TAITrasladoCard from "./TAITrasladoCard";

const { Text } = Typography;

const TAITrasladosList = ({
  traslados,
  selectedTraslados,
  onSelectTraslado,
  onSelectAll,
  onRefresh,
  hasPermission,
  onContextMenu,
}) => {
  // Agrupar traslados por fecha
  const trasladosAgrupados = useMemo(() => {
    const grupos = {};
    traslados.forEach((traslado) => {
      const fecha = traslado.fecha;
      if (!grupos[fecha]) {
        grupos[fecha] = [];
      }
      grupos[fecha].push(traslado);
    });
    return grupos;
  }, [traslados]);

  // Ordenar fechas
  const fechasOrdenadas = useMemo(() => {
    return Object.keys(trasladosAgrupados).sort();
  }, [trasladosAgrupados]);

  // Contar traslados pendientes seleccionables
  const pendientesCount = useMemo(() => {
    return traslados.filter((t) => t.tai_estado_asignacion === "pendiente").length;
  }, [traslados]);

  // Verificar si todos los pendientes están seleccionados
  const allSelected = useMemo(() => {
    const pendientes = traslados.filter(
      (t) => t.tai_estado_asignacion === "pendiente"
    );
    return (
      pendientes.length > 0 &&
      pendientes.every((t) => selectedTraslados.includes(t.id))
    );
  }, [traslados, selectedTraslados]);

  // Verificar si hay algún seleccionado
  const someSelected = useMemo(() => {
    return selectedTraslados.length > 0 && !allSelected;
  }, [selectedTraslados, allSelected]);

  if (traslados.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            No hay traslados TAI para los filtros seleccionados
          </span>
        }
        style={{ marginTop: 40 }}
      />
    );
  }

  return (
    <div className="tai-traslados-list">
      {/* Header con selección masiva */}
      {hasPermission("ASIGNAR_TAI") && pendientesCount > 0 && (
        <div className="tai-list-header">
          <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
          >
            <Text strong>
              Seleccionar todos los pendientes ({pendientesCount})
            </Text>
          </Checkbox>
          {selectedTraslados.length > 0 && (
            <Tag color="purple" icon={<CarOutlined />}>
              {selectedTraslados.length} seleccionados
            </Tag>
          )}
        </div>
      )}

      {/* Lista agrupada por fecha */}
      {fechasOrdenadas.map((fecha) => (
        <div key={fecha} className="tai-fecha-group">
          <div className="tai-fecha-header">
            <Text strong className="tai-fecha-label">
              {dayjs(fecha).isValid()
                ? dayjs(fecha).format("dddd, D [de] MMMM [de] YYYY")
                : fecha}
            </Text>
            <Tag color="blue">{trasladosAgrupados[fecha].length} traslados</Tag>
          </div>

          <List
            dataSource={trasladosAgrupados[fecha]}
            renderItem={(traslado) => (
              <TAITrasladoCard
                key={traslado.id}
                traslado={traslado}
                isSelected={selectedTraslados.includes(traslado.id)}
                onSelect={(selected) => onSelectTraslado(traslado.id, selected)}
                onRefresh={onRefresh}
                hasPermission={hasPermission}
                onContextMenu={onContextMenu}
              />
            )}
            split={false}
            className="tai-traslados-inner-list"
          />
        </div>
      ))}
    </div>
  );
};

TAITrasladosList.propTypes = {
  traslados: PropTypes.array.isRequired,
  selectedTraslados: PropTypes.array.isRequired,
  onSelectTraslado: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func,
};

export default TAITrasladosList;
